import EmptyBeneficiaryContentSvg from "@/assets/images/emptyBeneficiaryContent.svg";
import type { IAccount } from "@/modules/account/common/types";
import type { IGroupTransferBeneficiary } from "@/modules/transaction/common/types";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import { useMediaQuery } from "@/shared/hooks";
import { AdvancedTable as Table } from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddEditBeneficiaryModal } from "./AddEditBeneficiaryModal";
import { getColumns } from "./beneficiaryColumns";
import { DeleteBeneficiaryModal } from "./DeleteBeneficiaryModal";

interface Props {
  groupTransferBeneficiariesList: IGroupTransferBeneficiary[];
  setGroupTransferBeneficiariesList: Dispatch<SetStateAction<IGroupTransferBeneficiary[]>>;
  accountsList: IAccount[];
}

const BENEFICIARY_MAX_LENGTH = 50;

export const GroupTransferBeneficiaryList = ({
  groupTransferBeneficiariesList,
  setGroupTransferBeneficiariesList,
  accountsList,
}: Props) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isAddEditBeneficiaryModalOpen, setAddEditBeneficiaryModalOpen] = useState<boolean>(false);
  const [rowIndex, setRowIndex] = useState<number | null>(null);
  const [row, setRow] = useState<IGroupTransferBeneficiary | null>(null);
  const [isEditRow, setIsEditRow] = useState<boolean>(false);

  const onOpenDeleteModal = (index: number) => {
    setRowIndex(index);
    setIsDeleteModalOpen(true);
  };

  const onDelete = () => {
    if (rowIndex !== null) {
      const list = groupTransferBeneficiariesList.toSpliced(rowIndex, 1);
      setGroupTransferBeneficiariesList(list);
    }
    setIsDeleteModalOpen(false);
  };

  const onEdit = (data: IGroupTransferBeneficiary, index: number) => {
    setRow(data);
    setRowIndex(index);
    setIsEditRow(true);
    setAddEditBeneficiaryModalOpen(true);
  };

  const columns = getColumns({
    t,
    onEdit,
    onDelete: onOpenDeleteModal,
    isDesktop,
  });

  const isBeneficiaryMaxSize = groupTransferBeneficiariesList.length >= BENEFICIARY_MAX_LENGTH;

  useEffect(() => {
    if (!isAddEditBeneficiaryModalOpen) {
      setIsEditRow(false);
    }
  }, [isAddEditBeneficiaryModalOpen]);

  return (
    <>
      <Table
        data={groupTransferBeneficiariesList}
        isActionsVisible={!isDesktop}
        columns={columns}
        defaultPageSize={50}
        emptyTitle={t("transfer.group.emptyBeneficiaryList")}
        emptyIllustration={EmptyBeneficiaryContentSvg}
        emptyButtonProps={{
          buttonText: t("transfer.group.addBeneficiary"),
          iconProps: {
            Component: (props: ISVGIconProps) => (
              <CloudFlareImage
                {...props}
                className="empty-beneficiary-add-button"
                path={CloudflareAsset.Add}
              />
            ),
          },
          onClick: () => setAddEditBeneficiaryModalOpen(true)
        }}
        renderHeader={() => (
          <div className="advanced-table__header flexbox justify-content--end py-16 px-24">
            <div className="flexbox">
              {groupTransferBeneficiariesList?.length ? (
                <>
                  {isBeneficiaryMaxSize && (
                    <Tooltip
                      position={Positions.TOP_CENTER}
                      id="add-edit-beneficiary-button"
                      text={t("transfer.group.beneficiariesMaxLength")}
                    />
                  )}
                  <Button
                    iconProps={{
                      Component: (props: ISVGIconProps) => (
                        <CloudFlareImage {...props} path={CloudflareAsset.Add}/>
                      ),
                    }}
                    id="add-edit-beneficiary-button"
                    disabled={isBeneficiaryMaxSize}
                    data-testid="open-add-beneficiary-modal"
                    data-ga-id="open-add-beneficiary-modal"
                    data-ga-action="Open add beneficiary modal"
                    onClick={() => setAddEditBeneficiaryModalOpen(true)}
                    type="secondary"
                    buttonText={t("transfer.group.addBeneficiary")}
                  />
                </>
              ) : (
                <span className="py-20"/>
              )}
            </div>
          </div>
        )}
        renderFooter={() => (
          <div className="advanced-table__footer">
            <div className="full-width flexbox justify-content--end">
              <Text type="tertiary">
                {`${t("general.total")}: ${groupTransferBeneficiariesList?.length}`}
              </Text>
            </div>
          </div>
        )}
      />
      <DeleteBeneficiaryModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onSubmit={onDelete}
      />
      <AddEditBeneficiaryModal
        isOpen={isAddEditBeneficiaryModalOpen}
        groupTransferBeneficiariesList={groupTransferBeneficiariesList}
        setGroupTransferBeneficiariesList={setGroupTransferBeneficiariesList}
        beneficiary={row}
        rowIndex={rowIndex}
        isEdit={isEditRow}
        accountsList={accountsList}
        onClose={() => setAddEditBeneficiaryModalOpen(false)}
      />
    </>
  );
};
